import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Chip, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNotify, useRecordContext } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { getAuthProviderFetchJson } from '../../lib/Auth';
import { superAdminApiServer } from '../../lib/config';
import { employeeCacheMock, requestCacheMock } from './cacheMockData';

const PAGE_SIZE = 50;

interface EmployeeCacheItem {
  externalId: string;
  matricula: string | null;
  name: string | null;
  status: string | null;
  isActive: boolean | null;
  startDate: string | null;
  deactivationDate: string | null;
  syncedAt: string | null;
  updateReason: string | null;
}

interface RequestCacheItem {
  externalId: string;
  employeeExternalId: string | null;
  raw: {
    requestType: string | null;
    requestSubtypeExternalId: string | null;
    startTime: string | null;
    endTime: string | null;
    approvalStatus: string | null;
  } | null;
  syncedAt: string | null;
  updateReason: string | null;
}

interface ReqFilters {
  externalId: string;
  employeeExternalId: string;
  startDate: string;
  endDate: string;
  requestSubtypeExternalId: string;
  requestType: string;
}

const EMPTY_REQ_FILTERS: ReqFilters = {
  externalId: '',
  employeeExternalId: '',
  startDate: '',
  endDate: '',
  requestSubtypeExternalId: '',
  requestType: ''
};

const EmployeeCacheView = ({ integrationUuid }: { integrationUuid: string }) => {
  const notify = useNotify();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [items, setItems] = useState<EmployeeCacheItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Atomically reset offset + commit search so the fetch effect always sees
  // offset=0 when debouncedSearch changes — preventing stale-offset fetches
  // and the empty-table / no-refetch bug when offset was already 0.
  useEffect(() => {
    const id = setTimeout(() => {
      setOffset(0);
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());

    if (process.env.NODE_ENV === 'development') {
      const query = debouncedSearch.trim().toLowerCase();
      const filtered = query ? employeeCacheMock.items.filter((i) => i.externalId.toLowerCase().includes(query) || (i.name ?? '').toLowerCase().includes(query) || (i.matricula ?? '').toLowerCase().includes(query)) : employeeCacheMock.items;
      const page = filtered.slice(offset, offset + PAGE_SIZE);
      if (!cancelled) {
        setItems((prev) => (offset === 0 ? page : [...prev, ...page]));
        setTotal(filtered.length);
        setLoading(false);
      }
      return;
    }

    const url = `${superAdminApiServer}/integrations/${integrationUuid}/cache/employees?${params}`;
    getAuthProviderFetchJson(url)
      .then(({ json }) => {
        if (!cancelled) {
          setItems((prev) => (offset === 0 ? json?.items ?? [] : [...prev, ...(json?.items ?? [])]));
          setTotal(json?.total ?? 0);
        }
      })
      .catch(() => {
        if (!cancelled) notify('Failed to load employee cache', { type: 'error' });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [integrationUuid, debouncedSearch, offset, notify]);

  const handleLoadMore = () => setOffset(items.length);

  const handleDelete = async (externalId: string) => {
    if (!window.confirm(`Delete cache entry for employee "${externalId}"? This will force a full re-sync for this employee on the next tick.`)) return;
    setDeleting(externalId);
    const url = `${superAdminApiServer}/integrations/${integrationUuid}/cache/employees/${encodeURIComponent(externalId)}`;
    try {
      await getAuthProviderFetchJson(url, { method: 'DELETE' });
      setItems((prev) => prev.filter((i) => i.externalId !== externalId));
      setTotal((prev) => prev - 1);
    } catch {
      notify('Failed to delete employee cache entry', { type: 'error' });
    } finally {
      setDeleting(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    const params = new URLSearchParams();
    if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
    const url = `${superAdminApiServer}/integrations/${integrationUuid}/cache/employees/export?${params}`;
    try {
      const { body, headers } = await getAuthProviderFetchJson(url);
      const contentDisposition = headers.get('content-disposition') ?? '';
      const match = contentDisposition.match(/filename="?([^";]+)"?/);
      const downloadName = match?.[1] ?? `employees-cache-${integrationUuid}.csv`;
      const blob = new Blob([body], { type: 'text/csv' });
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = downloadName;
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      notify('Failed to export employee cache', { type: 'error' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <TextField size="small" label="Search name / externalId / matricula" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 320 }} />
        <Button variant="outlined" size="small" startIcon={exporting ? <CircularProgress size={14} /> : <DownloadIcon />} disabled={exporting} onClick={handleExport}>
          Export CSV
        </Button>
        {loading && <CircularProgress size={20} />}
        {!loading && (
          <Typography variant="caption" color="text.secondary">
            {total} total
          </Typography>
        )}
      </Box>

      {!loading && items.length === 0 && (
        <Typography color="text.secondary" fontStyle="italic">
          No employee cache entries found.
        </Typography>
      )}

      {items.length > 0 && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>External ID</TableCell>
                <TableCell>Matricula</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>Deactivation Date</TableCell>
                <TableCell>Synced At</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.externalId} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/integrations/${integrationUuid}/employees-cache/${encodeURIComponent(item.externalId)}`)}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{item.externalId}</TableCell>
                  <TableCell>{item.matricula ?? '—'}</TableCell>
                  <TableCell>{item.name ?? '—'}</TableCell>
                  <TableCell>{item.status ? <Chip label={item.status} color={item.isActive === false ? 'default' : 'success'} size="small" /> : '—'}</TableCell>
                  <TableCell>{item.startDate ?? '—'}</TableCell>
                  <TableCell>{item.deactivationDate ?? '—'}</TableCell>
                  <TableCell>{item.syncedAt ? new Date(item.syncedAt).toLocaleString() : '—'}</TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Remove this employee from cache, forcing a full re-sync on the next tick">
                      <span>
                        <Button size="small" color="error" disabled={deleting === item.externalId} onClick={() => handleDelete(item.externalId)} startIcon={deleting === item.externalId ? <CircularProgress size={14} /> : undefined}>
                          Clear
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button startIcon={loading ? <CircularProgress size={14} /> : <ExpandMoreIcon />} disabled={loading || items.length >= total} onClick={handleLoadMore}>
              Load more ({items.length}/{total})
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

const RequestCacheView = ({ integrationUuid }: { integrationUuid: string }) => {
  const notify = useNotify();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ReqFilters>(EMPTY_REQ_FILTERS);
  const [committed, setCommitted] = useState<ReqFilters>(EMPTY_REQ_FILTERS);
  const [items, setItems] = useState<RequestCacheItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (committed.externalId) params.set('externalId', committed.externalId);
    if (committed.employeeExternalId) params.set('employeeExternalId', committed.employeeExternalId);
    if (committed.startDate) params.set('startDate', committed.startDate);
    if (committed.endDate) params.set('endDate', committed.endDate);
    if (committed.requestSubtypeExternalId) params.set('requestSubtypeExternalId', committed.requestSubtypeExternalId);
    if (committed.requestType) params.set('requestType', committed.requestType);

    if (process.env.NODE_ENV === 'development') {
      let filtered = requestCacheMock.items;
      if (committed.externalId) filtered = filtered.filter((i) => i.externalId.toLowerCase().includes(committed.externalId.toLowerCase()));
      if (committed.employeeExternalId) filtered = filtered.filter((i) => (i.employeeExternalId ?? '').toLowerCase().includes(committed.employeeExternalId.toLowerCase()));
      if (committed.requestType) filtered = filtered.filter((i) => (i.raw?.requestType ?? '').toLowerCase().includes(committed.requestType.toLowerCase()));
      if (committed.requestSubtypeExternalId) filtered = filtered.filter((i) => (i.raw?.requestSubtypeExternalId ?? '').toLowerCase().includes(committed.requestSubtypeExternalId.toLowerCase()));
      if (committed.startDate) filtered = filtered.filter((i) => i.raw?.startTime && i.raw.startTime >= committed.startDate);
      if (committed.endDate) filtered = filtered.filter((i) => i.raw?.endTime && i.raw.endTime <= committed.endDate + 'T23:59:59Z');
      const page = filtered.slice(offset, offset + PAGE_SIZE);
      if (!cancelled) {
        setItems((prev) => (offset === 0 ? page : [...prev, ...page]));
        setTotal(filtered.length);
        setLoading(false);
      }
      return;
    }

    const url = `${superAdminApiServer}/integrations/${integrationUuid}/cache/requests?${params}`;
    getAuthProviderFetchJson(url)
      .then(({ json }) => {
        if (!cancelled) {
          setItems((prev) => (offset === 0 ? json?.items ?? [] : [...prev, ...(json?.items ?? [])]));
          setTotal(json?.total ?? 0);
        }
      })
      .catch(() => {
        if (!cancelled) notify('Failed to load request cache', { type: 'error' });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [integrationUuid, committed, offset, notify]);

  const handleFilter = (field: keyof ReqFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    setItems([]);
    setOffset(0);
    setCommitted({ ...filters });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleLoadMore = () => setOffset((prev) => prev + PAGE_SIZE);

  const handleExport = async () => {
    setExporting(true);
    const params = new URLSearchParams();
    if (committed.externalId) params.set('externalId', committed.externalId);
    if (committed.employeeExternalId) params.set('employeeExternalId', committed.employeeExternalId);
    if (committed.startDate) params.set('startDate', committed.startDate);
    if (committed.endDate) params.set('endDate', committed.endDate);
    if (committed.requestSubtypeExternalId) params.set('requestSubtypeExternalId', committed.requestSubtypeExternalId);
    if (committed.requestType) params.set('requestType', committed.requestType);
    const url = `${superAdminApiServer}/integrations/${integrationUuid}/cache/requests/export?${params}`;
    try {
      const { body, headers } = await getAuthProviderFetchJson(url);
      const contentDisposition = headers.get('content-disposition') ?? '';
      const match = contentDisposition.match(/filename="?([^";]+)"?/);
      const downloadName = match?.[1] ?? `requests-cache-${integrationUuid}.csv`;
      const blob = new Blob([body], { type: 'text/csv' });
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = downloadName;
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      notify('Failed to export request cache', { type: 'error' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <TextField size="small" label="External ID" value={filters.externalId} onChange={(e) => handleFilter('externalId', e.target.value)} onKeyDown={handleKeyDown} sx={{ minWidth: 180 }} />
        <TextField size="small" label="Employee External ID" value={filters.employeeExternalId} onChange={(e) => handleFilter('employeeExternalId', e.target.value)} onKeyDown={handleKeyDown} sx={{ minWidth: 180 }} />
        <TextField size="small" label="Request Type" value={filters.requestType} onChange={(e) => handleFilter('requestType', e.target.value)} onKeyDown={handleKeyDown} sx={{ minWidth: 160 }} />
        <TextField size="small" label="Subtype External ID" value={filters.requestSubtypeExternalId} onChange={(e) => handleFilter('requestSubtypeExternalId', e.target.value)} onKeyDown={handleKeyDown} sx={{ minWidth: 180 }} />
        <TextField size="small" type="date" label="Start Date ≥" value={filters.startDate} onChange={(e) => handleFilter('startDate', e.target.value)} onKeyDown={handleKeyDown} InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }} />
        <TextField size="small" type="date" label="End Date ≤" value={filters.endDate} onChange={(e) => handleFilter('endDate', e.target.value)} onKeyDown={handleKeyDown} InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }} />
        <Button variant="outlined" size="small" onClick={handleSearch} disabled={loading}>
          Search
        </Button>
        <Button variant="outlined" size="small" startIcon={exporting ? <CircularProgress size={14} /> : <DownloadIcon />} disabled={exporting} onClick={handleExport}>
          Export CSV
        </Button>
        {loading && <CircularProgress size={20} sx={{ alignSelf: 'center' }} />}
        {!loading && (
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {total} total
          </Typography>
        )}
      </Box>

      {!loading && items.length === 0 && (
        <Typography color="text.secondary" fontStyle="italic">
          No request cache entries found.
        </Typography>
      )}

      {items.length > 0 && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>External ID</TableCell>
                <TableCell>Employee External ID</TableCell>
                <TableCell>Request Type</TableCell>
                <TableCell>Subtype External ID</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>End Time</TableCell>
                <TableCell>Approval Status</TableCell>
                <TableCell>Synced At</TableCell>
                <TableCell>Update Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.externalId} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/integrations/${integrationUuid}/requests-cache/${encodeURIComponent(item.externalId)}`)}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{item.externalId}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{item.employeeExternalId ?? '—'}</TableCell>
                  <TableCell>{item.raw?.requestType ?? '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{item.raw?.requestSubtypeExternalId ?? '—'}</TableCell>
                  <TableCell>{item.raw?.startTime ? new Date(item.raw.startTime).toLocaleString() : '—'}</TableCell>
                  <TableCell>{item.raw?.endTime ? new Date(item.raw.endTime).toLocaleString() : '—'}</TableCell>
                  <TableCell>{item.raw?.approvalStatus ?? '—'}</TableCell>
                  <TableCell>{item.syncedAt ? new Date(item.syncedAt).toLocaleString() : '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{item.updateReason ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button startIcon={loading ? <CircularProgress size={14} /> : <ExpandMoreIcon />} disabled={loading || items.length >= total} onClick={handleLoadMore}>
              Load more ({items.length}/{total})
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

const CacheTab = () => {
  const record = useRecordContext();
  const [cacheType, setCacheType] = useState<'employees' | 'requests'>('employees');

  const integrationUuid = record?.uuid;
  if (!integrationUuid) return null;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={cacheType}
          exclusive
          onChange={(_, val) => {
            if (val) setCacheType(val);
          }}
          size="small"
        >
          <ToggleButton value="employees">Employees</ToggleButton>
          <ToggleButton value="requests">Requests</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {cacheType === 'employees' ? <EmployeeCacheView key={integrationUuid} integrationUuid={integrationUuid} /> : <RequestCacheView key={integrationUuid} integrationUuid={integrationUuid} />}
    </Box>
  );
};

export default CacheTab;
